import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, X, Upload, FileText, Image } from 'lucide-react';

export default function AdminProducts() {
  const { apiClient } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', price: '', category: '' });
  const [uploading, setUploading] = useState({});

  const fetchProducts = useCallback(async () => {
    try { const { data } = await apiClient.get('/api/products'); setProducts(data); }
    catch { toast.error('Failed'); }
    finally { setLoading(false); }
  }, [apiClient]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/products', { ...form, price: parseFloat(form.price) });
      toast.success('Created!');
      setShowForm(false);
      setForm({ title: '', description: '', price: '', category: '' });
      fetchProducts();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await apiClient.delete(`/api/admin/products/${id}`); toast.success('Deleted'); fetchProducts(); }
    catch { toast.error('Failed'); }
  };

  const uploadFileFor = async (productId, file, field) => {
    setUploading(prev => ({ ...prev, [`${productId}-${field}`]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await apiClient.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await apiClient.patch(`/api/admin/products/${productId}`, { [field]: data.path });
      toast.success(`${field === 'pdf_path' ? 'PDF' : 'Cover'} uploaded!`);
      fetchProducts();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(prev => ({ ...prev, [`${productId}-${field}`]: false })); }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20";
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="text-2xl font-heading font-bold text-white">Products</h1><p className="text-white/50 text-sm">{products.length} resources</p></div>
        <button onClick={() => setShowForm(!showForm)} className="bg-gradient-to-r from-violet-500 to-violet-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2" data-testid="add-product-btn">{showForm ? <X size={16} /> : <Plus size={16} />} {showForm ? 'Cancel' : 'Add Product'}</button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="mb-8 p-6 rounded-2xl border border-white/8 bg-white/[0.03] space-y-4" data-testid="product-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input placeholder="Product Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className={inputCls} data-testid="product-title-input" />
            <input type="number" step="0.01" placeholder="Price ($)" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required className={inputCls} data-testid="product-price-input" />
          </div>
          <textarea placeholder="Description (2 lines brief)" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required rows="2" className={inputCls + " resize-none"} />
          <input placeholder="Category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} required className={inputCls} />
          <button type="submit" className="bg-gradient-to-r from-emerald-500 to-emerald-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold">Create Product</button>
        </form>
      )}

      {loading ? <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white/40" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map(p => (
            <div key={p.id} className="rounded-2xl border border-white/8 bg-white/[0.03] overflow-hidden" data-testid={`admin-product-${p.id}`}>
              {/* Cover Image */}
              <div className="h-40 bg-white/[0.02] flex items-center justify-center relative group">
                {p.cover_image_path ? (
                  <img src={`${API_URL}/api/files/${p.cover_image_path}`} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <Image size={32} className="text-white/10" />
                )}
                <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                  <span className="text-white text-xs font-semibold flex items-center gap-1"><Upload size={14} /> {uploading[`${p.id}-cover_image_path`] ? 'Uploading...' : 'Upload Cover'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadFileFor(p.id, e.target.files[0], 'cover_image_path'); }} />
                </label>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-white font-heading font-bold text-base flex-1">{p.title}</h3>
                  <button onClick={() => handleDelete(p.id)} className="p-1 text-white/20 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
                <p className="text-white/50 text-xs mb-3 line-clamp-2">{p.description}</p>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-violet-400 font-bold text-lg">${p.price}</span>
                  <span className="px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 text-xs font-bold">{p.category}</span>
                </div>
                {/* PDF Upload */}
                <div className="border-t border-white/5 pt-3">
                  {p.pdf_path ? (
                    <div className="flex items-center gap-2 text-green-300 text-xs"><FileText size={14} /> PDF uploaded</div>
                  ) : (
                    <label className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-dashed border-white/15 cursor-pointer hover:border-violet-400/50 text-white/40 text-xs">
                      <Upload size={14} /> {uploading[`${p.id}-pdf_path`] ? 'Uploading...' : 'Upload PDF/Book'}
                      <input type="file" accept=".pdf" className="hidden" onChange={e => { if (e.target.files[0]) uploadFileFor(p.id, e.target.files[0], 'pdf_path'); }} />
                    </label>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
